// utils/isEditorEmpty.ts
// Determine if a Lexical editor JSON string (or compressed format) is visually empty.
//
// Supports TWO formats:
// 1. Full Lexical state: {"root":{"children":[...]}}
// 2. Compressed format: {"nodes":[{"nodes":[...]}]}

type SerializedSubstate = {
    state: string;
};

// Full Lexical format types
type BaseNode = { type: string; children?: SerializedNode[] };
type TextNode = BaseNode & { type: 'text'; text?: string };
type ParagraphNode = BaseNode & { type: 'paragraph' };
type FractionJSON = BaseNode & {
    type: 'fraction';
    num?: SerializedSubstate;
    den?: SerializedSubstate;
};
type ExponentJSON = BaseNode & {
    type: 'exponent';
    base?: SerializedSubstate;
    exp?: SerializedSubstate;
};
type RootJSON = BaseNode & {
    type: 'root';
    radicand?: SerializedSubstate;
    index?: SerializedSubstate | null;
};
type OverlineJSON = BaseNode & {
    type: 'overline';
    content?: SerializedSubstate;
    variant?: 'segment' | 'line' | 'ray';
};
type SerializedNode =
    | TextNode
    | ParagraphNode
    | FractionJSON
    | ExponentJSON
    | RootJSON
    | OverlineJSON
    | BaseNode;

// Compressed format types
type CompressedNode =
    | { t: 'text'; c: string }
    | { t: 'fraction'; num: string; den: string }
    | { t: 'exponent'; base: string; exp: string }
    | { t: 'root'; rad: string; idx?: string }
    | { t: 'overline'; content: string; variant?: 'segment' | 'line' | 'ray' };

type CompressedParagraph = {
    nodes: CompressedNode[];
};

type CompressedEditorState = {
    nodes: CompressedParagraph[];
};

/** Public: pass the editor JSON string (either format) */
export function isEquationEditorEmpty(json: unknown): boolean {
    const s = narrowString(json);
    if (!s) return true;

    const parsed = safeParse<any>(s);
    if (!parsed) return true;

    // Check if it's compressed format
    if (parsed.nodes && Array.isArray(parsed.nodes)) {
        // Check if nodes array has CompressedParagraph structure
        const firstNode = parsed.nodes[0];
        if (firstNode && firstNode.nodes !== undefined) {
            // It's compressed format: {"nodes":[{"nodes":[...]}]}
            return isCompressedEditorEmpty(parsed as CompressedEditorState);
        }
    }

    // Check if it's full Lexical format
    if (parsed.root) {
        const children = parsed.root.children ?? [];
        return areNodesVisuallyEmpty(children);
    }

    // Unknown format - treat as empty
    return true;
}

/** Check if compressed format is empty */
function isCompressedEditorEmpty(state: CompressedEditorState): boolean {
    if (!state.nodes || state.nodes.length === 0) return true;

    for (const paragraph of state.nodes) {
        if (paragraph.nodes && paragraph.nodes.length > 0) {
            for (const node of paragraph.nodes) {
                if (!isCompressedNodeEmpty(node)) {
                    return false;
                }
            }
        }
    }

    return true;
}

/** Check if a single compressed node is empty */
function isCompressedNodeEmpty(node: CompressedNode): boolean {
    if (node.t === 'text') {
        const text = (node as any).c ?? '';
        return text.trim().length === 0;
    }

    if (node.t === 'fraction') {
        const frac = node as any;
        const numEmpty = isCompressedSubstateEmpty(frac.num);
        const denEmpty = isCompressedSubstateEmpty(frac.den);
        return numEmpty && denEmpty;
    }

    if (node.t === 'exponent') {
        const exp = node as any;
        const baseEmpty = isCompressedSubstateEmpty(exp.base);
        const expEmpty = isCompressedSubstateEmpty(exp.exp);
        return baseEmpty && expEmpty;
    }

    if (node.t === 'root') {
        const root = node as any;
        const radEmpty = isCompressedSubstateEmpty(root.rad);
        const idxEmpty = root.idx ? isCompressedSubstateEmpty(root.idx) : true;
        return radEmpty && idxEmpty;
    }

    if (node.t === 'overline') {
        const overline = node as any;
        return isCompressedSubstateEmpty(overline.content);
    }

    // Unknown node type - treat as non-empty to be safe
    return false;
}

/** Check if a compressed substate (string) is empty */
function isCompressedSubstateEmpty(substate: string): boolean {
    if (!substate) return true;

    const trimmed = substate.trim();
    if (!trimmed) return true;

    // Try to parse as JSON array (nested format)
    const parsed = safeParse<CompressedNode[]>(trimmed);
    if (Array.isArray(parsed)) {
        return areCompressedNodesEmpty(parsed);
    }

    // Not JSON - treat as plain text
    return trimmed.length === 0;
}

/** Check if an array of compressed nodes is empty */
function areCompressedNodesEmpty(nodes: CompressedNode[]): boolean {
    if (nodes.length === 0) return true;

    for (const node of nodes) {
        if (!isCompressedNodeEmpty(node)) {
            return false;
        }
    }

    return true;
}

/** Full Lexical format: recursively check node arrays */
function areNodesVisuallyEmpty(nodes: SerializedNode[]): boolean {
    for (const n of nodes) {
        if (!isNodeVisuallyEmpty(n)) {
            return false;
        }
    }
    return true;
}

/** Full Lexical format: check a single node */
function isNodeVisuallyEmpty(n: SerializedNode): boolean {
    // 1) Text nodes: any non-whitespace text → not empty
    if (n.type === 'text') {
        const t = (n as TextNode).text ?? '';
        return t.trim().length === 0;
    }

    // 2) Math nodes
    if (n.type === 'fraction') {
        const f = n as FractionJSON;
        const numEmpty = isSubstateEmpty(f.num);
        const denEmpty = isSubstateEmpty(f.den);
        return numEmpty && denEmpty;
    }

    if (n.type === 'exponent') {
        const e = n as ExponentJSON;
        const baseEmpty = isSubstateEmpty(e.base);
        const expEmpty = isSubstateEmpty(e.exp);
        return baseEmpty && expEmpty;
    }

    if (n.type === 'root') {
        const r = n as RootJSON;
        const radEmpty = isSubstateEmpty(r.radicand);
        const idxEmpty = r.index == null ? true : isSubstateEmpty(r.index);
        return radEmpty && idxEmpty;
    }

    if (n.type === 'overline') {
        const o = n as OverlineJSON;
        return isSubstateEmpty(o.content);
    }

    // 3) Containers (paragraphs, spans, etc.): check children
    const kids = n.children ?? [];
    if (kids.length > 0) {
        return areNodesVisuallyEmpty(kids);
    }

    // Unknown/leaf container with no children → empty
    return true;
}

/** Full Lexical format: check substate */
function isSubstateEmpty(sub?: SerializedSubstate | null): boolean {
    if (!sub || typeof sub.state !== 'string') return true;

    const stateStr = sub.state.trim();
    if (!stateStr) return true;

    // Try to parse as JSON
    const parsed = safeParse<any>(stateStr);
    if (!parsed) {
        // Not valid JSON - treat as plain text
        return stateStr.trim().length === 0;
    }

    // Check if it's a compressed array format (new nested format)
    if (Array.isArray(parsed)) {
        return areCompressedNodesEmpty(parsed);
    }

    // Check if it's a full Lexical state (old format)
    if (parsed.root) {
        return isEquationEditorEmpty(stateStr);
    }

    // Unknown format - treat as non-empty to be safe
    return false;
}

/** Utilities */
function safeParse<T>(s: string): T | null {
    try {
        return JSON.parse(s) as T;
    } catch {
        return null;
    }
}

function narrowString(v: unknown): string | null {
    if (typeof v === 'string') return v.trim();
    return null;
}
